/**
 * attachableFileTypes — what the file picker will let a user choose.
 *
 * WHY THIS EXISTS (QA Series C, Test 17, 2026-09-07)
 *
 * Malaika downloaded five test files and could not attach four of them. The
 * picker simply did not show them: the `accept` attribute on the file input
 * filters by extension, and `.wma`, `.weba`, `.m4b` and `.amr` were not in the
 * list. The server had just been taught to read all four — so the capability
 * existed, was tested, was deployed, and was UNREACHABLE.
 *
 * That is the whole failure mode this module prevents. The accept list was a
 * hand-maintained duplicate of the server's reader capability, copied into
 * THREE places (chat config, the Hidalgo widget, the widget's defaults), and
 * it had drifted from what the platform can actually read by 27 extensions:
 *
 *   audio    opus aiff m4b weba wma amr caf      (7 — all readable)
 *   video    m4v qt 3gp 3g2 wmv flv mpg mpeg     (10 — all readable)
 *            mts m2ts
 *   3D/CAD   ifc ply                             (2 — readable since 09-07)
 *   archive  rar 7z tar gz tgz bz2 xz            (7 — named honestly)
 *   text     log                                 (1)
 *
 * Some of that drift predates the formats added on 2026-09-07: `.opus` and
 * `.aiff` have been transcribable for a long time and were never attachable.
 * A user with an iPhone voice memo (.caf), an Android one (.amr), a dictaphone
 * file (.wma) or a browser recording (.weba) simply could not hand it over.
 *
 * THE RULE: if the server can read it, the user can attach it. A format the
 * server cannot read is still worth accepting when the refusal is HONEST and
 * names the way round — `.dwg` and `.rar` are here for that reason, because
 * "I can't open a .rar, send the files individually and I'll read each one"
 * is a better outcome than a picker that hides the file and explains nothing.
 *
 * A test in `functions/src/__tests__` reads the server's own extension maps
 * and this list, and fails the build when a readable format is not attachable.
 * That is what stops the two sides drifting apart a second time.
 */

/** Text and data files read as text. */
const TEXT = ['txt', 'md', 'csv', 'tsv', 'json', 'xml', 'html', 'htm', 'log', 'rtf'];

/** Office and portable documents, incl. the OpenDocument family. */
const DOCUMENTS = [
  'pdf', 'doc', 'docx', 'odt',
  'xls', 'xlsx', 'xlsm', 'ods',
  'ppt', 'pptx', 'odp',
];

/** Images — read by vision, and OCR'd when they carry text. */
const IMAGES = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'tiff', 'tif', 'svg', 'heic', 'heif'];

/**
 * Recordings. Every one of these is in the server's transcribable map
 * (`audioTranscript.AUDIO_MIME_BY_EXTENSION`). The last five are what real
 * dictation kit and phone recorders produce, and were unattachable until now.
 */
const AUDIO = [
  'mp3', 'wav', 'm4a', 'aac', 'flac', 'ogg', 'opus', 'aiff',
  'm4b', 'weba', 'wma', 'amr', 'caf',
];

/**
 * Video. `mp4`/`mov`/`m4v`/`3gp` additionally have their container facts read
 * locally — duration, resolution, whether there is sound — so they answer
 * something even when the footage cannot be watched.
 */
const VIDEO = [
  'mp4', 'm4v', 'mov', 'qt', '3gp', '3g2',
  'webm', 'avi', 'mkv', 'wmv', 'flv', 'mpg', 'mpeg', 'mts', 'm2ts',
];

/**
 * 3D and CAD. `stl obj step stp gltf glb dxf ifc ply` are described;
 * `dwg` is refused BY NAME with the conversion route, which is why it stays
 * on the list rather than being hidden.
 */
const MODELS = ['stl', 'obj', 'step', 'stp', 'gltf', 'glb', 'dxf', 'ifc', 'ply', 'dwg'];

/**
 * Archives. A disclosure bundle from opposing counsel IS a zip, and a zip is
 * expanded into the files inside it. The rest are named honestly ("not read;
 * sending the files individually works"), which is why they are accepted.
 */
const ARCHIVES = ['zip', 'rar', '7z', 'tar', 'gz', 'tgz', 'bz2', 'xz'];

/** Every extension the picker offers, without the leading dot. */
export const ATTACHABLE_EXTENSIONS: readonly string[] = [
  ...TEXT, ...DOCUMENTS, ...IMAGES, ...AUDIO, ...VIDEO, ...MODELS, ...ARCHIVES,
];

/**
 * MIME FAMILIES, offered alongside the extensions (2026-09-11).
 *
 * WHY, and why this does not contradict the paragraph below.
 *
 * On 2026-09-11 Malaika, on an iPhone with the app installed to the Home
 * Screen, could not get her screenshots into a group thread — nobody saw
 * them, herself included. On iOS Safari the picker's "Photo Library" and
 * "Take Photo or Video" entries are offered from the MIME types in `accept`
 * that Safari can resolve; a list of ~80 bare extensions, many of them UTIs
 * Safari has never heard of, is the shape that collapses the sheet to a
 * Files browser. Screenshots live in Photos, not Files.
 *
 * The rule the next paragraph states is about SPECIFIC MIME types, and it is
 * still right: `audio/mp4` in place of `.m4b` HIDES the user's file, which is
 * the whole failure this module exists to prevent. A WILDCARD family is the
 * opposite operation — it can only ever cause the picker to offer MORE. So
 * the families ride ALONGSIDE the full extension list, never instead of it,
 * and nothing that was attachable before is attachable less.
 *
 * Only families here. A specific type (`image/heic`, `audio/mp4`, …) belongs
 * nowhere in this file; the test asserts that property rather than the
 * spelling, so a specific type cannot creep back in beside a wildcard.
 */
const MIME_FAMILIES: readonly string[] = ['image/*', 'video/*', 'audio/*'];

/**
 * The `accept` attribute value for a file input.
 *
 * Extensions FIRST and in full: MIME types in `accept` are unreliable for
 * exactly the formats that matter here — browsers report `.m4b` as
 * `audio/mp4`, `.weba` sometimes as nothing at all, and a `.dxf` or `.ifc` as
 * `application/octet-stream`. An extension list is what actually shows the
 * user their file. The wildcard families above are appended so a phone also
 * offers the camera roll; see MIME_FAMILIES for why the two are compatible.
 *
 * THE FAMILIES ARE NOW THE ONLY THING THAT REACHES THE CAMERA (2026-09-12).
 * A second, `image/*`-only photo button shipped beside the paperclip on
 * 2026-09-11 and was removed the next day as redundant (owner: "all
 * attachments should be able to be attached from the attachment icon"). It
 * WAS redundant — the families in this one string are what make iOS Safari
 * offer Photo Library / Take Photo or Video / Choose File instead of
 * collapsing to the Files browser, and that fix shipped in the same change as
 * the button. So deleting these three entries would not merely narrow the
 * picker: it would restore the original defect, with no second entry point
 * left to paper over it. A test asserts their presence as a property.
 *
 * And still never `capture`: that attribute FORCES the camera and removes the
 * library from the sheet, which is the opposite of the choice being offered.
 */
export const ACCEPTED_FILE_TYPES: string = [
  ...ATTACHABLE_EXTENSIONS.map((ext) => `.${ext}`),
  ...MIME_FAMILIES,
].join(',');
